import * as React from 'react';

export interface IJsonViewProps {
  value: unknown;
  depth?: number;
  parentKey?: string;
  /** True when this value is followed by another sibling — render a trailing comma after the value's last character. */
  trailingComma?: boolean;
  /**
   * Keys whose array value collapses behind a `[…N items]` toggle. Applied only
   * to direct children of the payload root (depth 1) so a same-named array
   * nested deeper inside the payload still renders in full.
   */
  collapseArrayKeys?: ReadonlySet<string>;
  /**
   * Keys whose string value renders quoted but with a caller-supplied color
   * class instead of the default `text-fg-primary` — used to flag specific
   * fields (e.g. a tool call's `goal` green, `justification` magenta, or prose
   * keys promoted to bold bright-white). Applied at any depth.
   */
  stringKeyClasses?: ReadonlyMap<string, string>;
  /**
   * Keys whose string value is truncated to a short preview (`"abc…"`, full
   * value on hover) instead of rendered in full — for opaque, unbreakable
   * blobs like `thinkingSignature` that would otherwise overflow the page
   * width. Applied at any depth. Highest precedence among the string
   * treatments.
   */
  ellipsizeStringKeys?: ReadonlySet<string>;
}

interface ICollapsibleJsonArrayProps {
  length: number;
  /** Trailing comma to render after the collapsed summary (the expanded body carries its own). */
  comma: React.ReactNode;
  /** Fully-expanded array rendering, shown once the operator opens the node. */
  children: React.ReactNode;
}

/**
 * A JSON array that starts collapsed behind a `[…N items] ▸` toggle. Used for
 * arrays a caller names via `collapseArrayKeys` (e.g. a `toolResult` `content`
 * block that just duplicates its sibling `details`).
 */
const CollapsibleJsonArray = ({ length, comma, children }: ICollapsibleJsonArrayProps) => {
  const [collapsed, setCollapsed] = React.useState(true);
  if (collapsed) {
    return (
      <>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="text-fg-quaternary hover:text-fg-secondary cursor-pointer"
        >
          [
          <span className="text-fg-tertiary">
            …{length} item{length === 1 ? '' : 's'}
          </span>
          ] ▸
        </button>
        {comma}
      </>
    );
  }
  return (
    <span>
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        className="text-fg-quaternary hover:text-fg-secondary cursor-pointer"
      >
        ▾{' '}
      </button>
      {children}
    </span>
  );
};

/**
 * Hanging-indent style for a line whose value is a string: wrapped
 * continuation lines align under the value's opening quote instead of
 * collapsing to the block's left edge. `startCol` is the character column
 * where the value begins — the font is monospace, so `ch` units map 1:1
 * to columns. The negative `text-indent` cancels the `padding-left` on the
 * first line (the literal-space indent + key still position it), while
 * every wrapped line falls back to `padding-left`.
 */
const hangingIndentStyle = (startCol: number): React.CSSProperties => ({
  paddingLeft: `${startCol}ch`,
  textIndent: `-${startCol}ch`,
});

/** Preview length (chars) for an ellipsized string value before the trailing `…`. */
const MAX_ELLIPSIZED_STRING_LEN = 48;

/**
 * Tiny recursive JSON renderer. Keys are muted; primitives are colored by type.
 * The caller's container is expected to set `whitespace-pre-wrap` so embedded
 * newlines and the indentation this emits are preserved.
 *
 * Trailing commas are threaded through `trailingComma` rather than appended by
 * the parent — that way the comma always lands on the value's last visual line
 * (next to the closing `}` / `]`, or right after the primitive), never dangling
 * on a fresh line below a block-level child.
 *
 * Pass `collapseArrayKeys` to start named top-level arrays collapsed behind a
 * toggle, and `stringKeyClasses` to recolor chosen string fields (see
 * `IJsonViewProps`).
 */
const JsonView = ({
  value,
  depth = 0,
  parentKey,
  trailingComma = false,
  collapseArrayKeys,
  stringKeyClasses,
  ellipsizeStringKeys,
}: IJsonViewProps) => {
  const comma = trailingComma ? <span className="text-fg-quaternary">,</span> : null;

  if (value === null) {
    return (
      <>
        <span className="text-fg-error-primary">null</span>
        {comma}
      </>
    );
  }
  if (value === undefined) {
    return (
      <>
        <span className="text-fg-quaternary">undefined</span>
        {comma}
      </>
    );
  }
  if (typeof value === 'string') {
    if (
      parentKey !== undefined &&
      (ellipsizeStringKeys?.has(parentKey) ?? false) &&
      value.length > MAX_ELLIPSIZED_STRING_LEN
    ) {
      return (
        <>
          <span className="text-fg-tertiary" title={value}>
            &quot;{value.slice(0, MAX_ELLIPSIZED_STRING_LEN)}…&quot;
          </span>
          {comma}
        </>
      );
    }
    const colorClass = parentKey !== undefined ? stringKeyClasses?.get(parentKey) : undefined;
    if (colorClass !== undefined) {
      return (
        <>
          <span className={colorClass}>&quot;{value}&quot;</span>
          {comma}
        </>
      );
    }
    return (
      <>
        <span className="text-fg-primary">&quot;{value}&quot;</span>
        {comma}
      </>
    );
  }
  if (typeof value === 'number') {
    return (
      <>
        <span className="text-fg-brand-primary">{value}</span>
        {comma}
      </>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <>
        <span className="text-fg-warning-primary">{String(value)}</span>
        {comma}
      </>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <>
          <span className="text-fg-quaternary">[]</span>
          {comma}
        </>
      );
    }
    const indent = '  '.repeat(depth + 1);
    const closeIndent = '  '.repeat(depth);
    const body = (
      <span>
        <span className="text-fg-quaternary">[</span>
        {value.map((item, i) => (
          <span
            key={i}
            className="block"
            style={typeof item === 'string' ? hangingIndentStyle(indent.length) : undefined}
          >
            <span className="text-fg-quaternary">{indent}</span>
            <JsonView
              value={item}
              depth={depth + 1}
              trailingComma={i < value.length - 1}
              collapseArrayKeys={collapseArrayKeys}
              stringKeyClasses={stringKeyClasses}
              ellipsizeStringKeys={ellipsizeStringKeys}
            />
          </span>
        ))}
        <span className="block">
          <span className="text-fg-quaternary">{closeIndent}]</span>
          {comma}
        </span>
      </span>
    );
    // Only collapse a top-level payload key (depth 1) so a same-named array
    // nested deeper inside the payload still renders in full.
    if (depth === 1 && parentKey !== undefined && (collapseArrayKeys?.has(parentKey) ?? false)) {
      return (
        <CollapsibleJsonArray length={value.length} comma={comma}>
          {body}
        </CollapsibleJsonArray>
      );
    }
    return body;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return (
        <>
          <span className="text-fg-quaternary">{'{}'}</span>
          {comma}
        </>
      );
    }
    const indent = '  '.repeat(depth + 1);
    const closeIndent = '  '.repeat(depth);
    return (
      <span>
        <span className="text-fg-quaternary">{'{'}</span>
        {entries.map(([key, val], i) => (
          <span
            key={key}
            className="block"
            style={typeof val === 'string' ? hangingIndentStyle(indent.length + key.length + 2) : undefined}
          >
            <span className="text-fg-quaternary">{indent}</span>
            <span className="text-fg-secondary">{key}</span>
            <span className="text-fg-quaternary">: </span>
            <JsonView
              value={val}
              depth={depth + 1}
              parentKey={key}
              trailingComma={i < entries.length - 1}
              collapseArrayKeys={collapseArrayKeys}
              stringKeyClasses={stringKeyClasses}
              ellipsizeStringKeys={ellipsizeStringKeys}
            />
          </span>
        ))}
        <span className="block">
          <span className="text-fg-quaternary">
            {closeIndent}
            {'}'}
          </span>
          {comma}
        </span>
      </span>
    );
  }
  // Fallback for any unhandled type (shouldn't happen for JSONB — `Object`
  // scalar values are always JSON-shaped). Stringify defensively so a
  // future scalar widening doesn't crash the renderer.
  return (
    <>
      <span className="text-fg-quaternary">{JSON.stringify(value)}</span>
      {comma}
    </>
  );
};

export default JsonView;
